import { z } from 'zod';
import { documentNumberingModeSchema } from './document-numbering';
import { posDdtPassivoDetailSchema } from './ddt-passivo-inputs';

/**
 * HTTP query contracts for the two DDT list endpoints.
 *
 * The values are coerced once at the HTTP boundary.  DDT quantities are
 * deliberately not constrained to integers: documentary delivery lines may
 * contain fractional quantities.
 */
const optionalDate = z.string().date().optional();
const optionalUuid = z.string().uuid().optional();
const optionalFiniteNumber = z.preprocess(
  (value) => {
    if (value === undefined) return undefined;
    // Number('') is zero, which is not a faithful interpretation of an empty
    // HTTP query value. Deliberately leave it invalid instead.
    if (typeof value === 'string') return value.trim() === '' ? Number.NaN : Number(value);
    return value;
  },
  z.number().finite().optional(),
);
const optionalWireBoolean = z.preprocess(
  (value) => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
  },
  z.boolean().optional(),
);

const posDdtListQueryObjectSchema = z.object({
  status: z.string().max(64).optional(),
  causale: z.string().max(64).optional(),
  customer_id: optionalUuid,
  from_date: optionalDate,
  to_date: optionalDate,
  /** Trimmed, case-insensitive literal substring of the saved DDT number or recipient; blank means no filter. */
  q: z.string().max(255).optional(),
  min_value: optionalFiniteNumber,
  max_value: optionalFiniteNumber,
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(500).optional(),
}).strict();
export const posDdtListQuerySchema = posDdtListQueryObjectSchema.superRefine((query, ctx) => {
  if (query.min_value !== undefined && query.max_value !== undefined && query.min_value > query.max_value) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['max_value'],
      message: 'max_value must be greater than or equal to min_value',
    });
  }
});
export type PosDdtListQuery = z.infer<typeof posDdtListQuerySchema>;

const posDdtPassivoListQueryObjectSchema = z.object({
  status: z.string().max(255).optional(),
  supplier_id: optionalUuid,
  from_date: optionalDate,
  to_date: optionalDate,
  purchase_order_id: optionalUuid,
  supplier_invoice_id: optionalUuid,
  q: z.string().max(255).optional(),
  min_value: optionalFiniteNumber,
  max_value: optionalFiniteNumber,
  import_source: z.enum(['ai', 'api', 'manual']).optional(),
  archived: z.enum(['active', 'archived', 'all']).optional(),
  /** Restrict the goods-receipt picker to documents with a positive physical residual. */
  receivable: optionalWireBoolean,
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(500).optional(),
}).strict();
export const posDdtPassivoListQuerySchema = posDdtPassivoListQueryObjectSchema.superRefine((query, ctx) => {
  if (query.min_value !== undefined && query.max_value !== undefined && query.min_value > query.max_value) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['max_value'],
      message: 'max_value must be greater than or equal to min_value',
    });
  }
});
export type PosDdtPassivoListQuery = z.infer<typeof posDdtPassivoListQuerySchema>;

const wireNumeric = z.union([z.string(), z.number()]);
const wireDate = z.string().nullable();
const wireTimestamp = z.string().nullable();

/**
 * Documentary net amount, excluding VAT; never a current inventory valuation.
 * Active: saved quantity × saved price. Passive: apply the saved line discount
 * once, with the canonical purchase net-unit precision (4 decimals).
 * PostgreSQL rounds the sum to 2 decimals at document level; the backend emits
 * only a finite number (including zero), or null for an empty document or a
 * positive-quantity line with no saved price. No catalog-price fallback.
 */
const documentValue = z.number().finite().nullable();

const passivePurchaseOrderLinkSchema =
  posDdtPassivoDetailSchema.shape.purchase_orders.removeDefault().element.strict();
const passiveSupplierInvoiceLinkSchema =
  posDdtPassivoDetailSchema.shape.supplier_invoices.removeDefault().element.strict();

export const posDdtListItemSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  ddt_number: z.string(),
  numbering_mode: documentNumberingModeSchema.optional(),
  ddt_date: wireTimestamp,
  causale: z.string(),
  customer_id: z.string().nullable(),
  customer_name: z.string().nullable(),
  destination_address: z.string().nullable(),
  destination_city: z.string().nullable(),
  destination_province: z.string().nullable(),
  destination_zip: z.string().nullable(),
  warehouse_id: z.string().nullable(),
  carrier_name: z.string().nullable(),
  carrier_plate: z.string().nullable(),
  appearance: z.string().nullable(),
  transport_mode: z.string().nullable(),
  packages: wireNumeric.nullable(),
  weight_kg: wireNumeric.nullable(),
  notes: z.string().nullable(),
  sale_id: z.string().nullable(),
  status: z.string(),
  created_by: z.string().nullable(),
  created_at: wireTimestamp,
  updated_at: wireTimestamp,
  company_id: z.string(),
  is_valorizzato: z.boolean(),
  warehouse_name: z.string().nullable(),
  document_value: documentValue,
}).strict();
export type PosDdtListItem = z.infer<typeof posDdtListItemSchema>;

/**
 * The passive list header intentionally starts from the established detail
 * header contract.  The list adds only fields selected by its explicit SQL
 * projection; scalar PO/invoice aliases are not part of the current schema
 * (their documentary graph is represented by the two link arrays).
 */
export const posDdtPassivoListItemSchema = posDdtPassivoDetailSchema
  .omit({ items: true })
  .extend({
    ddt_date: wireDate,
    purchase_orders: z.array(passivePurchaseOrderLinkSchema),
    supplier_invoices: z.array(passiveSupplierInvoiceLinkSchema),
    received_at: wireTimestamp,
    created_by: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    attachments: z.unknown(),
    archived_at: wireTimestamp,
    company_id: z.string().nullable(),
    warehouse_name: z.string().nullable(),
    supplier_name_joined: z.string().nullable(),
    document_value: documentValue,
  })
  .strict();
export type PosDdtPassivoListItem = z.infer<typeof posDdtPassivoListItemSchema>;

export const posDdtListPaginationSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
});
export type PosDdtListPagination = z.infer<typeof posDdtListPaginationSchema>;

export const posDdtListDataSchema = z.object({
  items: z.array(posDdtListItemSchema),
  pagination: posDdtListPaginationSchema,
});
export type PosDdtListData = z.infer<typeof posDdtListDataSchema>;

export const posDdtPassivoListDataSchema = z.object({
  items: z.array(posDdtPassivoListItemSchema),
  pagination: posDdtListPaginationSchema,
});
export type PosDdtPassivoListData = z.infer<typeof posDdtPassivoListDataSchema>;

const responseMetaSchema = z.object({
  timestamp: z.union([z.string(), z.date()]),
  request_id: z.string(),
  tenant_id: z.string().optional(),
}).passthrough();

export const posDdtListResponseSchema = z.object({
  success: z.literal(true),
  data: posDdtListDataSchema,
  meta: responseMetaSchema,
});
export type PosDdtListResponse = z.infer<typeof posDdtListResponseSchema>;

export const posDdtPassivoListResponseSchema = z.object({
  success: z.literal(true),
  data: posDdtPassivoListDataSchema,
  meta: responseMetaSchema,
});
export type PosDdtPassivoListResponse = z.infer<typeof posDdtPassivoListResponseSchema>;
